import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, MapPin, ShieldCheck, ArrowLeft, Download, Star } from "lucide-react";
import Logo from "../components/Logo";
import { api } from "../lib/api";
import { downloadShareCard } from "../lib/shareCard";

const RANK_COLORS = ["bg-amber-100 text-amber-700 border-amber-200", "bg-gray-100 text-gray-300 border-gray-200", "bg-orange-100 text-orange-700 border-orange-200"];

export default function TopAgents() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => {
    try { const { data } = await api.get("/public/top-agents"); setRows(data); }
    finally { setLoading(false); }
  })(); }, []);

  const monthName = new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" });

  return (
    <div className="min-h-screen" data-testid="top-agents-page" style={{ background: "#080808", color: "#F5F5F5" }}>
      <header className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-gray-500 hover:text-white"><ArrowLeft className="h-4 w-4" /> <Logo size="sm" /></Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/find-agent" className="text-gray-500 hover:text-white" data-testid="link-find-agent">Find a Broker</Link>
            <Link to="/login" className="text-[#F97316] font-medium">Login</Link>
          </div>
        </div>
      </header>

      <section className="hero-glow text-white">
        <div className="max-w-5xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-white/70"><Trophy className="h-3.5 w-3.5 text-amber-300" /> {monthName}</div>
          <h1 className="mt-6 text-5xl md:text-6xl font-bold font-display tracking-tight">India's top rental agents & landlords.</h1>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto text-lg leading-relaxed">Ranked by rating, deals closed, and most recently rated on Orello. Every rating is verified. Every deal is real. No paid placements, no fake reviews.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-white/50">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#F97316]" /> Aadhaar-verified agents & landlords only</div>
            <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[#F97316]" /> All ratings are now public</div>
            <div className="flex items-center gap-2"><Trophy className="h-4 w-4 text-[#F97316]" /> Sorted by rating, deals, and most recently rated</div>
          </div>
          <p className="mt-4 text-xs text-white/30">Privacy-first: only first name + last initial shown. Brokers have opted in to be listed.</p>
        </div>
      </section>

      <section className="py-16" style={{ background: "#111111" }}>
        <div className="max-w-3xl mx-auto px-6">
          {loading ? <div className="text-center text-gray-500">Loading…</div> : rows.length === 0 ? (
            <div className="text-center py-16">
              <Trophy className="h-12 w-12 mx-auto text-gray-300" />
              <h2 className="mt-4 text-xl font-semibold font-display">No leaderboard yet this month</h2>
              <p className="text-gray-500 mt-2 text-sm">Be the first agent to close a deal. <Link to="/register" className="text-[#F97316] font-medium">Sign up</Link></p>
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((r, i) => (
                <div key={i} className="card-ink p-5 flex items-center gap-4 hover:shadow-md transition-shadow" data-testid={`leaderboard-row-${i}`}>
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center text-lg font-bold border ${RANK_COLORS[i] || "bg-[#FFF4ED] text-indigo-900 border-indigo-100"}`}>#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold flex items-center gap-2">{r.display_name} <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /></div>
                    <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {r.city}</span>
                      {r.rating && (<><span>·</span><span className="inline-flex items-center gap-0.5 text-amber-600"><Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {r.rating.avg} <span className="text-gray-400">({r.rating.count})</span></span></>)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold font-display">{r.deals_this_month}</div>
                    <div className="text-[11px] text-gray-500 uppercase tracking-wider">deals this month</div>
                  </div>
                  <button
                    onClick={() => downloadShareCard({ rank: i + 1, displayName: r.display_name, city: r.city, dealsThisMonth: r.deals_this_month, monthLabel: monthName })}
                    className="ml-2 inline-flex items-center gap-1.5 text-xs font-medium border border-gray-200 hover:border-indigo-300 hover:bg-[#FFF4ED] hover:text-indigo-900 px-3 py-2 rounded-lg btn-press"
                    data-testid={`share-${i}`}
                    title="Download share card"
                  >
                    <Download className="h-3.5 w-3.5" /> Share card
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="mt-12 card-ink p-6 bg-[#FFF4ED]/40 border-indigo-100">
            <h3 className="font-semibold font-display">Want to be on the leaderboard?</h3>
            <p className="text-sm text-gray-400 mt-1">Join Orello as an agent or landlord, opt in from your profile, and start closing deals. Ratings are now public for all users.</p>
            <div className="mt-3 flex gap-2">
              <Link to="/find-agent" className="text-sm text-[#F97316] font-medium">Find an agent →</Link>
              <span className="text-gray-300">·</span>
              <Link to="/register" className="text-sm text-[#F97316] font-medium">Become an agent</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#0F0F1A] text-white/50 py-8 text-center text-sm">© 2025 Orello — No deposit fights.</footer>
    </div>
  );
}
